import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { DashboardApi } from 'services';
import { toastMessages } from 'config/constants';
import { useCompany, useCompanyFunctions } from 'contexts/CompanyContext';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import moment from 'moment';
import momentFR from 'moment/min/locales';
import routes from 'config/routes';
import toast from 'react-hot-toast';

moment.locale('fr', momentFR);

const configSteps = [
  {
    text: 'Décrivez votre entreprise',
    link: routes.register.description.root,
    field: 'description_created',
    state: null,
  },
  { text: 'Créez un moniteur', link: routes.register.config.root, field: 'employee_created', state: null },
  {
    text: 'Renseignez le lieu de vos activités',
    link: routes.register.place.root,
    field: 'place_created',
    state: null,
  },
  {
    text: 'Créez votre première activité',
    link: routes.register.activities.root,
    field: 'service_created',
    state: null,
  },
  { text: 'Paramétrez l’agenda', link: routes.register.agenda.root, field: 'agenda_created', state: { showEnd: true } },
];

const adminSteps = [
  { text: 'Complétez vos informations d’identité', link: routes.register.identity.root, field: 'identity_check' },
  { text: 'Chargez votre KBIS', link: routes.register.kbis.root, field: 'kbis_check' },
  { text: 'Complétez vos informations d’assurance', link: routes.register.insurance.root, field: 'insurance_check' },
  { text: 'Complétez vos informations bancaires', link: routes.register.bank.root, field: 'bank_check' },
  {
    text: 'Devenez partenaire et signez votre contrat avec Maïa',
    link: routes.register.contract.root,
    field: 'contract_check',
  },
];

const notifsList = [
  { title: 'Nouveau message', text: 'Vous avez reçu un nouveau message', type: 'new_message' },
  { title: 'Nouvelle réservation', text: 'Vous avez une nouvelle réservation', type: 'new_booking' },
  {
    title: 'Annulation Réservation',
    text: 'un client a annulé sa réservation pour la prestation de',
    type: 'cancel_booking',
  },
  {
    title: 'Note par un client',
    text: 'Un client vient de vous attribuer la note de 4 étoiles pour la prestation de',
    type: 'note_client',
  },
  { title: 'Décaler une réservation', text: 'vous avez décalé la prestation de', type: 'shift_booking' },
  { title: 'Paramétrage de l’agenda', text: 'Bravo vous venez de paramétrer votre agenda', type: 'setting_agenda' },
  { title: 'Création d’un nouvel équipier', text: 'Bravo vous venez de créer un nouvel équipier', type: 'new_monitor' },
  {
    title: 'Création d’une nouvelle entreprise',
    text: 'Bravo vous venez de créer une nouvelle entreprise',
    type: 'new_company',
  },
  {
    title: 'Création d’une prestation',
    text: 'Félicitation vous venez de créer une nouvelle prestation',
    type: 'new_service',
  },
  {
    title: 'Ajout d’un client dans une réservation',
    text: 'Vous avez ajouté un client pour la prestation',
    type: 'new_booking_myself',
  },
  {
    title: 'Suppression d’un Client dans une réservation',
    text: ' Vous avez supprimé un client pour la prestation',
    type: 'cancel_booking_myself',
  },
];

const NotificationsList = ({ classes }) => {
  const history = useHistory();
  const location = useLocation();
  const { palette } = useTheme();
  const { companySteps: steps, companyStepLoading: areStepsLoading } = useCompany();
  const { loadSteps } = useCompanyFunctions();

  const [areNotificationsLoading, setAreNotificationLoading] = useState(true);
  const [activeConfigStep, setActiveConfigStep] = useState(0);
  const [nbDoneAdminStep, setNBDoneAdminStep] = useState(0);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    getNotificationsHome();
  }, []);

  const countAdminSteps = useCallback(() => {
    let nb = 0;
    adminSteps.forEach((s) => (steps[s.field] ? (nb += 1) : null));
    setNBDoneAdminStep(nb);
  }, [steps]);

  const countConfigStep = useCallback(() => {
    let nb = 0;
    configSteps.forEach((s) => (steps[s.field] ? (nb += 1) : null));
    setActiveConfigStep(nb);
  }, [steps]);

  const getMinimalInactiveConfigStep = useCallback(() => {
    for (let index = 0; index < configSteps.length; index++) {
      if (!steps[configSteps[index].field]) return index;
    }
    return configSteps.length - 1;
  }, [steps]);

  useEffect(() => {
    setTimeout(loadSteps);
  }, [loadSteps, location.pathname]);

  useEffect(() => {
    countAdminSteps();
    countConfigStep();
  }, [countAdminSteps, countConfigStep, steps]);

  const areStepsCompleted = activeConfigStep >= configSteps.length && nbDoneAdminStep >= adminSteps.length;
  const showLoader = areStepsLoading || (areStepsCompleted && areNotificationsLoading);

  const getNotificationsHome = () => {
    setAreNotificationLoading(true);
    DashboardApi.getNotificationsHome()
      .then(({ notifications }) => {
        const data = notifications.map((notification) => {
          const notificationIndex = notifsList.find((n) => n.type === notification.type_notification);
          if (notificationIndex) {
            return {
              messageType: notification.type_notification,
              messageText: notificationIndex.text,
              messageTitle: notificationIndex.title,
              id: notification.id,
              date: moment(notification.date).format('dddd Do MMMM HH:mm'),
              serviceName: notification.service_name,
              dateServiceStart: moment(notification.date_service_start).format('dddd Do MMMM à HH:mm'),
            };
          }

          return false;
        });

        setNotifs(data.reverse());
      })
      .catch(() => {
        toast.error(toastMessages.error.FETCH_DASHBOARD_NOTIFICATIONS);
      })
      .finally(() => setAreNotificationLoading(false));
  };

  const deleteNotificationHome = (id) => {
    setAreNotificationLoading(true);
    DashboardApi.deleteNotificationHome(id)
      .then(() => {
        setNotifs(notifs.filter((item) => item.id !== id));
        setAreNotificationLoading(false);
        toast.success(toastMessages.success.DELETE_DASHBOARD_NOTIFICATION);
      })
      .catch(() => {
        setAreNotificationLoading(false);
        toast.error(toastMessages.error.DELETE_DASHBOARD_NOTIFICATION);
      });
  };

  const colorStep = (state, totalStep) => {
    if (state < 1) return 'red';
    else if (state < totalStep) return 'orange';
    return 'green';
  };

  return (
    <Card className={classes.cardRoot}>
      <CardContent>
        {showLoader && (
          <Grid container direction="column" justifyContent="center" alignItems="center" style={{ height: '33vh' }}>
            <CircularProgress />
          </Grid>
        )}

        {!showLoader &&
          (!areStepsCompleted ? (
            <>
              <div>
                <Typography style={{ color: colorStep(activeConfigStep, configSteps.length) }}>
                  Configuration {activeConfigStep}/{configSteps.length}
                </Typography>
                <List>
                  <ListItem
                    button
                    disabled={activeConfigStep >= configSteps.length}
                    onClick={() =>
                      history.push(
                        configSteps[getMinimalInactiveConfigStep()].link,
                        configSteps[getMinimalInactiveConfigStep()].state,
                      )
                    }
                    style={
                      activeConfigStep < configSteps.length
                        ? { color: palette.text.title }
                        : { color: palette.text.title, textDecoration: 'line-through' }
                    }
                  >
                    <ListItemIcon>
                      {activeConfigStep < configSteps.length ? <HourglassEmptyIcon color="primary" /> : <CheckIcon />}
                    </ListItemIcon>
                    <ListItemText primary={configSteps[getMinimalInactiveConfigStep()].text} />
                  </ListItem>
                </List>
              </div>
              <div>
                <Typography style={{ color: colorStep(nbDoneAdminStep, adminSteps.length) }}>
                  Administratif {nbDoneAdminStep}/{adminSteps.length}
                </Typography>
                <List>
                  {adminSteps.map((s, index) => (
                    <ListItem
                      key={`adminStep-${index}`}
                      button
                      disabled={steps[s.field]}
                      onClick={() => history.push(s.link)}
                      style={
                        steps[s.field]
                          ? { color: palette.text.title, textDecoration: 'line-through' }
                          : { color: palette.text.title }
                      }
                    >
                      <ListItemIcon>
                        {steps[s.field] ? <CheckIcon /> : <HourglassEmptyIcon color="primary" />}
                      </ListItemIcon>
                      <ListItemText primary={s.text} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          ) : (
            <List>
              {notifs.map((s, index) => {
                let str = s.date + ', ' + s.messageText;
                str +=
                  s.messageType === 3 || s.messageType === 9 || s.messageType === 10
                    ? ' ' + s.serviceName + ' du ' + s.dateServiceStart
                    : '';
                return (
                  index < 4 && (
                    <ListItem key={`notif-${index}`}>
                      <ListItemText primary={s.messageTitle} secondary={str} />
                      <IconButton
                        onClick={() => {
                          deleteNotificationHome(s.id);
                        }}
                      >
                        <ClearOutlinedIcon color="error" />
                      </IconButton>
                    </ListItem>
                  )
                );
              })}
            </List>
          ))}
      </CardContent>
    </Card>
  );
};

export default NotificationsList;
